import axios from "axios";
import { saveRefreshToken, loadRefreshToken, clearRefreshToken } from "./tokenStore";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Add token to requests from localStorage as fallback
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses - skip for auth endpoints to avoid redirect loops
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const url = error.config?.url || "";
    const isAuthEndpoint = url.includes("/auth/");
    // During impersonation, the refresh cookie/localStorage still belong to
    // the admin. A 401-driven refresh here would clobber the impersonation
    // access token with a fresh admin token, silently breaking the preview.
    // Better to let the 401 propagate — the user can click Exit Preview to
    // bail cleanly.
    const isImpersonating = !!localStorage.getItem("token_original");
    if (error.response?.status === 401 && !error.config._retry && !isAuthEndpoint && !isImpersonating) {
      error.config._retry = true;
      try {
        // Read refresh_token from the triple-redundancy store (localStorage
        // first, IndexedDB fallback). httpOnly cookie also sent automatically
        // via withCredentials.
        const stored = await loadRefreshToken();
        const body = stored ? { refresh_token: stored } : {};
        const { data } = await axios.post(`${API}/auth/refresh`, body, { withCredentials: true });
        if (data.token) localStorage.setItem("token", data.token);
        if (data.refresh_token) saveRefreshToken(data.refresh_token);
        error.config.headers.Authorization = `Bearer ${data.token}`;
        return api(error.config);
      } catch {
        localStorage.removeItem("token");
        clearRefreshToken();
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

// Auth
export const authAPI = {
  login: (data) => api.post("/auth/login", data),
  register: (data) => api.post("/auth/register", data),
  logout: () => api.post("/auth/logout"),
  me: () => api.get("/auth/me"),
  impersonate: (userId) => api.post("/auth/impersonate", { user_id: userId }),
  // Self-service password reset
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  getResetToken: (token) => api.get(`/auth/reset-password/${token}`),
  resetPassword: (token, password) => api.post("/auth/reset-password", { token, password }),
  // Authenticated password change (used by force-change-on-first-login flow)
  changePassword: (current_password, new_password) =>
    api.post("/auth/change-password", { current_password, new_password }),
};

// Expenses
export const expensesAPI = {
  list: (params) => api.get("/expenses", { params }),
  get: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post("/expenses", data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
  submit: (id) => api.post(`/expenses/${id}/submit`),
  approve: (id) => api.post(`/expenses/${id}/approve`),
  reject: (id, reason) => api.post(`/expenses/${id}/reject`, { reason: reason || "" }),
  pushToQbo: (id, force = false) => api.post(`/expenses/${id}/push-to-qbo${force ? "?force=true" : ""}`),
  pushToJobtread: (id, force = false) => api.post(`/expenses/${id}/push-to-jobtread${force ? "?force=true" : ""}`),
  push: (id, force = false) => api.post(`/expenses/${id}/push${force ? "?force=true" : ""}`),
  forceUnlinkJt: (id, reason) => api.post(`/expenses/${id}/force-unlink-jt`, { reason: reason || "" }),
};

// Upload
export const uploadAPI = {
  // Fast upload — saves the file and returns immediately (1-2s).
  // OCR is run separately via scanReceipt(storage_path) so it never blocks
  // the attachment landing (prevents dropped receipts on slow networks).
  receipt: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/upload/receipt?skip_ocr=true", formData, { headers: { "Content-Type": "multipart/form-data" } });
  },
  // Kick off AI OCR against an already-uploaded receipt. Best-effort — the
  // caller can render results when they land but doesn't block on this.
  scanReceipt: (storage_path) => api.post("/upload/receipt/ocr", { storage_path }),
};

// GL Accounts
export const glAccountsAPI = {
  list: (params) => api.get("/gl-accounts", { params }),
  create: (data) => api.post("/gl-accounts", data),
  delete: (id) => api.delete(`/gl-accounts/${id}`),
  toggleVisibility: (id) => api.patch(`/gl-accounts/${id}/visibility`),
  bulkVisibility: (ids, visible) => api.post(`/gl-accounts/bulk-visibility`, { ids, visible }),
  toggleAutoApprove: (id) => api.patch(`/gl-accounts/${id}/auto-approve`),
};

// Jobs
export const jobsAPI = {
  list: () => api.get("/jobs"),
  create: (data) => api.post("/jobs", data),
  update: (id, data) => api.patch(`/jobs/${id}`, data),
  delete: (id) => api.delete(`/jobs/${id}`),
};

// Budget Items (from JobTread; visibility is sourced from the per-item
// 'Display in Expenser' custom field in JT — no local toggle.)
export const budgetItemsAPI = {
  list: (jobId, visibleOnly) => api.get("/budget-items", { params: { job_id: jobId, visible_only: visibleOnly ? "true" : undefined } }),
};

// Vendors
export const vendorsAPI = {
  search: (q) => api.get("/vendors", { params: { q } }),
  list: () => api.get("/vendors"),
  listAll: () => api.get("/vendors", { params: { all: "true" } }),
  create: (data) => api.post("/vendors", data),
  delete: (id) => api.delete(`/vendors/${id}`),
};

// Mercury
export const mercuryAPI = {
  transactions: (params) => api.get("/mercury/transactions", { params }),
  simulateSwipe: (data) => api.post("/mercury/simulate-swipe", data || {}),
  link: (txnId, expenseId) => api.post(`/mercury/transactions/${txnId}/link`, { expense_id: expenseId }),
  webhookConfig: () => api.get("/mercury/webhook-config"),
  webhookHealth: () => api.get("/mercury/webhook-health"),
  rotateWebhookSecret: () => api.post("/mercury/webhook-secret/rotate"),
  clearWebhookSecret: () => api.delete("/mercury/webhook-secret"),
  suggestVendors: (force = false) => api.post(`/mercury/transactions/suggest-vendors${force ? "?force=true" : ""}`),
  // Gas pre-auth reconciliation
  reconcileGasPreauths: () => api.post("/mercury/transactions/reconcile-gas-preauths"),
  unresolvedPreauths: (days = 7) => api.get("/mercury/transactions/unresolved-preauths", { params: { days } }),
  linkPreauth: (realId, preauthId) => api.post(`/mercury/transactions/${realId}/link-preauth`, { preauth_mercury_id: preauthId }),
  unlinkPreauth: (txnId) => api.post(`/mercury/transactions/${txnId}/unlink-preauth`),
  deleteTransaction: (txnId) => api.delete(`/mercury/transactions/${txnId}`),
  dedupe: () => api.post("/mercury/transactions/dedupe"),
  listTombstoned: (days = 30) => api.get("/mercury/transactions/tombstoned", { params: { days } }),
  untombstone: (txnId) => api.post(`/mercury/transactions/${txnId}/untombstone`),
};

// QuickBooks Online
export const qboAPI = {
  status: () => api.get("/qbo/status"),
  connect: () => api.get("/qbo/connect"),
  disconnect: () => api.post("/qbo/disconnect"),
  test: () => api.post("/qbo/test"),
  syncAccounts: () => api.post("/qbo/sync-accounts"),
  syncCustomers: () => api.post("/qbo/sync-customers"),
  syncVendors: () => api.post("/qbo/sync-vendors"),
  listCustomers: () => api.get("/qbo/customers"),
};

// Dashboard
export const dashboardAPI = {
  stats: (period) => api.get("/dashboard/stats", { params: period ? { period } : {} }),
};

// Autopilot rules
export const autopilotAPI = {
  list: () => api.get("/autopilot-rules"),
  create: (data) => api.post("/autopilot-rules", data),
  update: (id, data) => api.patch(`/autopilot-rules/${id}`, data),
  delete: (id) => api.delete(`/autopilot-rules/${id}`),
  restoreDefaults: () => api.post("/autopilot-rules/restore-defaults"),
  fromExpense: (expenseId, maxAmount) => api.post(`/autopilot-rules/from-expense/${expenseId}`, { max_amount: maxAmount }),
};

// Settings
export const settingsAPI = {
  get: () => api.get("/settings/integrations"),
  update: (data) => api.put("/settings/integrations", data),
};

// Users
export const usersAPI = {
  list: () => api.get("/users"),
  get: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
  invite: (data) => api.post("/users/from-mercury-user", data),
  resendInvite: (id) => api.post(`/users/${id}/resend-invite`),
  sendPasswordReset: (id) => api.post(`/users/${id}/send-reset`),
  setTempPassword: (id) => api.post(`/users/${id}/set-temp-password`),
  addCard: (id, card) => api.post(`/users/${id}/cards`, card),
  removeCard: (id, cardId) => api.delete(`/users/${id}/cards/${cardId}`),
  syncQBO: () => api.post("/users/sync-qbo"),
  syncMercury: () => api.post("/users/sync-mercury"),
};

// Mercury Team / Users
export const mercuryUsersAPI = {
  list: () => api.get("/mercury/users"),
  sync: () => api.post("/mercury/sync-users"),
};

// Auth invite
export const inviteAPI = {
  get: (token) => api.get(`/auth/invite/${token}`),
  accept: (token, password) => api.post("/auth/accept-invite", { token, password }),
};

// Sync
export const syncAPI = {
  jobtreadJobs: () => api.post("/sync/jobtread-jobs"),
  jobtreadBudgetItems: () => api.post("/sync/jobtread-budget-items"),
  mercuryTransactions: () => api.post("/sync/mercury-transactions"),
  mercuryCards: () => api.post("/sync/mercury-cards"),
};

// Comments
export const commentsAPI = {
  list: (entityType, entityId) => api.get("/comments", { params: { entity_type: entityType, entity_id: entityId } }),
  create: (data) => api.post("/comments", data),
  delete: (id) => api.delete(`/comments/${id}`),
};

// File URL helper
export const getFileUrl = (path) => `${API}/files/${path}`;

// File download as blob
export const downloadFileBlob = async (path) => {
  const response = await api.get(`/files/${path}`, { responseType: "blob" });
  return URL.createObjectURL(response.data);
};

export default api;
